import ModalDialog from '@stone/components/modal/ModalDialog';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwiperCore, { EffectFade, Keyboard, Navigation, Thumbs, FreeMode, Pagination } from 'swiper';
import { Swiper as SwiperInstance, SwiperSlide } from 'swiper/react';
import imageutils, { SM, MD, LG, HIRES } from '@utils/imageutils';
import ProductGalleryImage from './ProductGalleryImage';
import useBreakpoint from '@common/utils/useBreakpoint';
import ProductVideo from './ProductVideo';
import classNames from 'classnames';
SwiperCore.use([Keyboard, Navigation, EffectFade, Thumbs, FreeMode, Pagination]);
const ProductGallery = (props) => {
    const { t } = useTranslation();
    const [swiperInstance, setSwiperInstance] = useState();
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const currentBreakpoint = useBreakpoint();
    const handleClose = () => {
        setThumbsSwiper(null);
        props.onRequestClose();
    };
    const sizeMainMap = {
        0: MD,
        375: MD,
        768: MD,
        1024: LG,
        1280: LG,
        1440: LG,
        1920: HIRES,
    };
    //dev level to debug with video on sandbox
    // const media: string[] = [
    //   'https://moncler-cdn.thron.com/delivery/public/video/moncler/H209Z1A00001M2904999_V2/dpx6uv/WEBFULLHD/H209Z1A00001M2904999_V2.mp4',
    //   'https://moncler-cdn.thron.com/delivery/public/video/moncler/H209Z1A00001M2904999_V1/dpx6uv/WEBFULLHD/H209Z1A00001M2904999_V1.mp4',
    //   'https://moncler-cdn.thron.com/delivery/public/image/moncler/H209Z1A00001M2904999_X/dpx6uv/std/0x0/H209Z1A00001M2904999_X.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw400a89ed/mbm/3-vion-special70-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    //   'https://staging2.cdn.moncler.ows.farm/on/demandware.static/-/Library-Sites-library-global/default/dw7335a406/mbm/3-vion-iconic-v2.jpg',
    // ];
    const media = props.media;
    // @ts-ignore
    const direction = useMemo(() => (currentBreakpoint >= window.mqObj.md ? 'vertical' : 'horizontal'), [currentBreakpoint]);
    const paginationMessage = t('carousel.paginationbulletmessage');
    const updateProgressThumb = (swiper) => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };
    return (React.createElement(ModalDialog, { noPadding: true, hideHeader: true, fullWidth: true, fullHeight: true, overlayClassName: "product-zoom-modal-overlay fade-in", className: "product-zoom-modal", isOpen: props.initialSlide >= 0, onRequestClose: handleClose, contentLabel: "gallery", onAfterClose: () => {
            if (props.scrollHeight) {
                window.scrollTo(0, props.scrollHeight);
            }
        } },
        React.createElement(SwiperInstance, { onSwiper: (swipr) => {
                setSwiperInstance(swipr);
            }, onSlideChange: (swiper) => {
                setActiveIndex(swiper.activeIndex);
            }, keyboard: true, direction: direction, navigation: {
                nextEl: '.carousel-gallery__arrow--arrow-right',
                prevEl: '.carousel-gallery__arrow--arrow-left',
            }, mousewheel: {
                forceToAxis: true,
                thresholdDelta: 20,
            }, allowTouchMove: false, className: "product-zoom swiper-container swiper-container--gallery", effect: "fade", initialSlide: props.initialSlide, speed: 1000, thumbs: { swiper: thumbsSwiper }, loop: true, loopPreventsSlide: false, slidesPerView: 1 }, media === null || media === void 0 ? void 0 : media.map((url, index) => {
            var _a, _b;
            return (React.createElement(SwiperSlide, { key: `${index}-${url.split('_').pop()}` },
                React.createElement(ProductGalleryImage, { index: index, media: media, isActiveIndex: activeIndex - 1 === index, src: imageutils.transform(url, sizeMainMap[currentBreakpoint], (_b = (_a = props.product) === null || _a === void 0 ? void 0 : _a.imgs) === null || _b === void 0 ? void 0 : _b.alt, null, 'avif'), isZoom: true }),
                React.createElement("div", { className: "swiper-lazy-preloader" })));
        })),
        React.createElement("div", { className: "product-zoom-thumbs--wrapper" },
            React.createElement(SwiperInstance, { onSwiper: (swiper) => {
                    setThumbsSwiper(swiper);
                    updateProgressThumb(swiper);
                }, onTransitionStart: updateProgressThumb, onTransitionEnd: updateProgressThumb, watchOverflow: true, spaceBetween: 12, direction: direction, slidesPerView: 6, watchSlidesProgress: true, modules: [Thumbs, FreeMode, Navigation], className: classNames('product-zoom-thumbs swiper-container', { start: isBeginning }, { end: isEnd }), freeMode: { enabled: true, sticky: true }, slideToClickedSlide: true, centerInsufficientSlides: true, grabCursor: true, initialSlide: props.initialSlide, 
                // loop
                // loopPreventsSlide={false}
                threshold: 20 }, media === null || media === void 0 ? void 0 : media.map((url, index) => (React.createElement(SwiperSlide, { key: `${index}+${url.split('_').pop()}` },
                React.createElement("button", { onClick: () => swiperInstance.slideTo(index + 1), className: "product-zoom-thumbs__thumb-button", "data-index": activeIndex, "aria-label": paginationMessage.replace('INDEX_NUMBER', `${index + 1}`) },
                    ['_V1', '_V2', '_V3', '_V4', '_V5', '_V6', '_V7', '_V8', '_V9', '_V10'].some((e) => url.includes(e)) ? (React.createElement(ProductVideo, { url: url })) : (React.createElement("img", { key: url, src: imageutils.transform(url, SM, ''), alt: "", loading: "lazy" })),
                    React.createElement("div", { className: "swiper-lazy-preloader" })))))))));
};
export default ProductGallery;
